<template>
  <div :class="b(modifiers)">
    <div :class="b('content')">
      <h5 :class="b('title')">
        {{ notification.title || $t(`c-notification.${notification.message.type}Title`) }}
      </h5>
      <div :class="b('message')">
        {{ notification.message.message }}
      </div>
      <div
        v-if="actions.length"
        :class="b('actions')"
      >
        <e-button
          v-for="action in actions"
          :key="action.modifier"
          :progress="action.progress"
          :disabled="action.progress"
          :variant="action.variant || 'secondary'"
          type="button"
          spacing="200"
          width="auto"
          @click="action.callback"
        >
          <span>{{ action.label }}</span>
          <e-icon-sprite
            v-if="action.icon"
            :class="b('action-icon')"
            :icon="action.icon"
            size="18"
          />
        </e-button>
      </div>
    </div>
    <button
      v-if="typeof notification.confirm !== 'function'"
      :class="b('close')"
      :aria-label="$t('c-notification.close')"
      type="button"
      @click="close"
    >
      <e-icon-sprite
        icon="i-close"
        size="16"
      />
    </button>
  </div>
</template>

<script>
  import useNotificationStore from '@/stores/notification';
  import eButton from '@/elements/e-button.vue';
  import eIconSprite from '@/elements/e-icon-sprite.vue';

  /**
   * Notification component to be used within c-notification-container. See /styleguide/notifications for demo.
   */
  export default {
    name: 'c-notification',
    components: {
      eIconSprite,
      eButton,
    },

    // mixins: [],

    props: {
      /**
       * Sets the display type which defines how the notifications are being rendered.
       *
       * Valid values: `[global, modal, field, add-to-cart, selector]`
       */
      displayType: {
        type: String,
        default: 'global',
        validator(value) {
          return ['global', 'modal'].includes(value);
        },
      },

      /**
       * The notification object consisting of the following properties:
       *
       * @param {Object} notification.message - The actual message data.
       * @param {String} notification.message.type - Type of response.
       * @param {String} notification.message.message - The actual response message from backend.
       * @param {Number} notification.id - Unique id.
       * @param {Object} [notification.message.meta] - Meta data for the message.
       * @param {String} [notification.title] - Adds title to the notification.
       * @param {Function} [notification.confirm] - Adds confirm button and gets called when the confirm button is clicked.
       * @param {Function} [notification.decline] - Adds decline button and gets called when the decline button is clicked.
       *
       */
      notification: {
        type: Object,
        required: true,
      },
    },
    setup() {
      return {
        notificationStore: useNotificationStore(),
      };
    },
    data() {
      return {
        /**
         * @type {Boolean} Defines if notification should be visible.
         */
        visible: true,

        /**
         * @type {Boolean} Handles en-/disabled state of progress animation for confirm button.
         */
        confirmProgress: false,

        /**
         * @type {Boolean} Handles en-/disabled state of progress animation for decline button.
         */
        declineProgress: false,

        /**
         * @type {Boolean} Handles the expire state for the component.
         */
        expire: false,
      };
    },

    computed: {
      /**
       * Returns all modifiers for the component main class.
       *
       * @returns {Object}
       */
      modifiers() {
        return {
          type: this.notification.message.type,
          confirm: this.notification.confirm,
          displayType: this.displayType,
          visible: this.visible,
          expire: this.expire,
        };
      },

      /**
       * Returns an Array of actions for the notification.
       *
       * @returns {Array.<Object>}
       */
      actions() {
        const { action, confirm, decline } = this.notification;
        const actions = [];

        if (action) {
          actions.push({
            ...action,
            modifier: 'action',
            progress: false,
            disabled: false,
            variant: 'primary',
            icon: 'i-chevron-2--right',
            callback: () => {
              this.close();
              action.callback?.();
            },
          });
        } else if (confirm) {
          if (decline) {
            actions.push({
              modifier: 'decline',
              label: this.$t('c-notification.decline'),
              progress: this.declineProgress,
              disabled: this.confirmProgress,
              callback: this.onDecline,
            });
          }

          actions.push({
            modifier: 'confirm',
            label: this.$t('c-notification.confirm'),
            progress: this.confirmProgress,
            disabled: this.declineProgress,
            variant: 'primary',
            callback: this.onConfirm,
          });
        }

        return actions;
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      const { expire, delay } = this.notification;

      if (expire) {
        const timeoutDelay = delay ? delay * 1000 : 3000;

        setTimeout(() => {
          this.close();
        }, timeoutDelay);
      }
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      this.$el.removeEventListener('animationend', this.onAnimationEnd);

      if (this.expire) {
        // If a custom action opens a modal, the standard close would never trigger the animation end event.
        this.notificationStore.popNotification(this.notification.id);
      }
    },
    // unmounted() {},

    methods: {
      /**
       * Callback handler for the animationend event when the notification is hiding.
       *
       * @param {Event} event - The original DOM event.
       */
      onAnimationEnd(event) {
        event.target.removeEventListener('animationend', this.onAnimationEnd);

        this.$nextTick(() => {
          this.notificationStore.popNotification(this.notification.id);
        });
      },

      /**
       * Removes current notification from stack.
       */
      close() {
        this.expire = true;

        this.$el.addEventListener('animationend', this.onAnimationEnd);
      },

      /**
       * Callback for confirm button click.
       */
      onConfirm() {
        const eventPromise = new Promise((resolve, reject) => {
          this.notification.confirm({
            notification: this.notification,
            resolve,
            reject,
          });
        });

        this.confirmProgress = true;

        eventPromise
          .then(() => {
            this.close();
          })
          .catch(() => {});
      },

      /**
       * Callback for delcine button click.
       */
      onDecline() {
        const eventPromise = new Promise((resolve, reject) => {
          this.notification.decline({
            notification: this.notification,
            resolve,
            reject,
          });
        });

        this.declineProgress = true;

        eventPromise
          .then(() => {
            this.close();
          })
          .catch(() => {});
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/extends' as *;

  @keyframes c-notification-animation-in {
    0% {
      right: -105%;
      max-height: 0;
    }

    50% {
      right: -105%;
      max-height: 200px;
    }

    100% {
      right: 0;
    }
  }

  @keyframes c-notification-animation {
    0% {
      right: 0;
    }

    50% {
      right: -105%;
      max-height: 200px;
    }

    100% {
      right: -105%;
      max-height: 0;
    }
  }

  .c-notification {
    $status-bar-width: 2rem;
    $border-width: 1px;
    $animation-duration: 0.6s;

    position: relative;
    display: none;
    width: 100%;
    border: functions.rem($border-width) solid variables.$color-grayscale--900;
    background: variables.$color-grayscale--1000;
    animation: c-notification-animation-in $animation-duration ease-out 0s forwards;
    animation-fill-mode: forwards;

    // Status
    &::before {
      display: flex;
      content: '';
      flex: 0 0 auto;
      justify-content: center;
      align-items: center;
      width: $status-bar-width;
      margin: functions.rem((-$border-width) 0 (-$border-width) (-$border-width));
      background-color: variables.$color-grayscale--500;
    }

    &::after {
      @include mixins.iconSprite('i-info--slim', 1.4rem, variables.$color-grayscale--1000);

      content: '';
      position: absolute;
      left: functions.rem(5px);
      height: 100% !important; // stylelint-disable-line declaration-no-important
      width: calc($status-bar-width - 10px) !important; // stylelint-disable-line declaration-no-important
    }

    &--type-success {
      &::before {
        background-color: variables.$color-status--success;
      }

      &::after {
        @include mixins.iconSprite('i-check', 1.4rem, variables.$color-grayscale--1000);
      }
    }

    &--type-warning {
      &::before {
        background-color: variables.$color-status--warning;
      }

      &::after {
        @include mixins.iconSprite('i-exclamation', 1.4rem, variables.$color-grayscale--1000);
      }
    }

    &--type-error {
      &::before {
        background-color: variables.$color-status--danger;
      }

      &::after {
        @include mixins.iconSprite('i-bolt', 1.4rem, variables.$color-grayscale--1000);
      }
    }

    &--display-type-modal {
      margin-bottom: 0;
      border-right: 0;
      border-left: 0;
      border-radius: 0;
      box-shadow: none;
    }

    &--expire {
      animation: c-notification-animation $animation-duration ease-out 0s forwards;
      animation-fill-mode: forwards;
    }

    &--visible {
      display: flex;
    }

    &__content {
      @include mixins.font(variables.$font-size--14);

      flex: 1 1 auto;
      padding: functions.rem(variables.$spacing--15 variables.$spacing--25);
      color: variables.$color-grayscale--100;
    }

    &__title {
      @include mixins.font-size(variables.$font-size--14);

      color: variables.$color-grayscale--100;
      font-weight: variables.$font-weight--semi-bold;

      @include mixins.media(md) {
        @include mixins.font-size(variables.$font-size--16);
      }
    }

    &__actions {
      display: flex;
      flex: 0 0 auto;
      align-self: center;
      margin: functions.rem(variables.$spacing--20 0 variables.$spacing--10);

      .e-button__inner {
        @include mixins.font(variables.$font-size--14);
      }
    }

    &__action + &__action {
      margin-left: variables.$spacing--5;
    }

    &__action-icon {
      width: functions.rem(18px);
      height: functions.rem(18px);
    }

    &__close {
      @extend %button-reset;

      display: flex;
      flex: 0 0 auto;
      flex-direction: column; // Needed by iOS Safari
      align-items: flex-start; // Needed by iOS Safari
      width: functions.rem(30px);
      padding: functions.rem(variables.$spacing--15 0);
      cursor: pointer;
      color: variables.$color-grayscale--500;

      &:hover {
        color: variables.$color-primary--1;
      }
    }

    .e-button__inner {
      display: flex;
      justify-content: center;
      align-items: center;

      .e-icon-sprite {
        margin-left: functions.rem(variables.$spacing--10);
      }
    }
  }
</style>
